import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertTriangle,
  CheckCircle2,
  MessageCircle,
  Pencil,
  Plus,
  Send,
  Trash2,
  Wifi,
} from 'lucide-react';
import {
  formatPhone,
  type ChannelCheck,
  type MessageChannel,
  type MessageRecipient,
  type Message,
} from '@amai/contracts';
import { api } from '@/lib/api';
import { errorMessage, fieldErrors } from '@/lib/errors';
import { formatDateTime } from '@/lib/format';
import { T } from '@/design/tokens';
import { WorkspaceHeader } from '@/components/WorkspaceHeader';
import { ReadField } from '@/components/ContextPanel';
import { StatusBadge } from '@/components/StatusBadge';
import { Button } from '@/components/Button';
import { ConfirmDialog, Drawer, Modal } from '@/components/Modal';
import { Field, FormSection } from '@/components/FormSection';
import { EmptyState, ErrorState, LoadingState } from '@/components/states';
import { useAuth } from '@/lib/auth-context';

/**
 * O canal de WhatsApp da empresa e quem recebe.
 *
 * Esta tela existe para que ligar o agente NÃO seja mexer em arquivo no
 * servidor. O token é o segredo mais perigoso do sistema depois do
 * certificado: entra aqui uma vez, some da tela e nunca mais volta.
 */
export function CanalPage() {
  const { can } = useAuth();
  const queryClient = useQueryClient();

  const [editing, setEditing] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [newRecipient, setNewRecipient] = useState(false);
  const [editingRecipient, setEditingRecipient] = useState<MessageRecipient | null>(null);
  const [removingRecipient, setRemovingRecipient] = useState<MessageRecipient | null>(null);
  const [testing, setTesting] = useState<MessageRecipient | null>(null);
  const [check, setCheck] = useState<ChannelCheck | null>(null);

  const channelQuery = useQuery({
    queryKey: ['message-channel'],
    queryFn: () => api.get<{ data: MessageChannel | null }>('/message-channel'),
  });
  const channel = channelQuery.data?.data ?? null;

  const recipientsQuery = useQuery({
    queryKey: ['message-recipients'],
    queryFn: () => api.get<{ data: MessageRecipient[] }>('/message-recipients'),
    enabled: Boolean(channel),
  });

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['message-channel'] });
    void queryClient.invalidateQueries({ queryKey: ['message-recipients'] });
  };

  const checkMutation = useMutation({
    mutationFn: () => api.post<ChannelCheck>('/message-channel/check'),
    onSuccess: (result) => {
      setCheck(result);
      void queryClient.invalidateQueries({ queryKey: ['message-channel'] });
    },
  });

  const removeMutation = useMutation({
    mutationFn: () => api.delete('/message-channel'),
    onSuccess: () => {
      setRemoving(false);
      refresh();
    },
  });

  const removeRecipientMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/message-recipients/${id}`),
    onSuccess: () => {
      setRemovingRecipient(null);
      refresh();
    },
  });

  const recipients = recipientsQuery.data?.data ?? [];

  return (
    <>
      <WorkspaceHeader
        title="Canal de mensagens"
        actions={
          can('canal-mensagens:editar') ? (
            <Button variant="primary" icon={Pencil} onClick={() => setEditing(true)}>
              {channel ? 'Editar canal' : 'Configurar canal'}
            </Button>
          ) : undefined
        }
      />

      <div className="workspace__body">
        {channelQuery.isPending && <LoadingState />}
        {channelQuery.isError && (
          <ErrorState
            message={errorMessage(channelQuery.error)}
            onRetry={() => void channelQuery.refetch()}
          />
        )}

        {channelQuery.data && !channel && (
          <EmptyState
            title="Nenhum canal configurado"
            description="Cadastre o número da empresa na API oficial da Meta para o sistema poder mandar mensagem. Você vai precisar do Phone Number ID, do WABA ID e de um token permanente."
            action={
              can('canal-mensagens:editar') ? (
                <Button variant="primary" icon={Plus} onClick={() => setEditing(true)}>
                  Configurar canal
                </Button>
              ) : undefined
            }
          />
        )}

        {channel && (
          <>
            <div className="card" style={{ padding: 18, marginBottom: 14 }}>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: 14,
                }}
              >
                <div className="panel__title" style={{ margin: 0 }}>
                  <MessageCircle size={15} style={{ verticalAlign: -2, marginRight: 6 }} aria-hidden />
                  {channel.name}
                </div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  {channel.isActive ? (
                    <StatusBadge kind="ok">Ligado</StatusBadge>
                  ) : (
                    <StatusBadge kind="mute">Desligado</StatusBadge>
                  )}
                  {can('canal-mensagens:editar') && (
                    <Button
                      small
                      variant="outline"
                      icon={Wifi}
                      loading={checkMutation.isPending}
                      onClick={() => checkMutation.mutate()}
                    >
                      Testar conexão
                    </Button>
                  )}
                  {can('canal-mensagens:excluir') && (
                    <Button
                      small
                      variant="ghost"
                      icon={Trash2}
                      aria-label="Remover canal"
                      title="Remover canal"
                      onClick={() => setRemoving(true)}
                    />
                  )}
                </div>
              </div>

              {(check || channel.lastCheckAt) && (
                <div
                  className={check?.ok ?? channel.lastCheckOk ? 'card' : 'login__alert'}
                  role={check?.ok ?? channel.lastCheckOk ? undefined : 'alert'}
                  style={{ padding: 10, marginBottom: 14, fontSize: 12.5 }}
                >
                  {(check?.ok ?? channel.lastCheckOk) ? (
                    <span style={{ color: T.green, display: 'inline-flex', gap: 6, alignItems: 'center' }}>
                      <CheckCircle2 size={14} aria-hidden />
                      O WhatsApp respondeu
                      {check?.verifiedName ? ` como "${check.verifiedName}"` : ''} em{' '}
                      {formatDateTime(check?.checkedAt ?? channel.lastCheckAt)}.
                    </span>
                  ) : (
                    <span style={{ display: 'inline-flex', gap: 6, alignItems: 'flex-start' }}>
                      <AlertTriangle size={14} aria-hidden style={{ marginTop: 1 }} />
                      {check?.error ?? channel.lastCheckError}
                    </span>
                  )}
                </div>
              )}

              {checkMutation.isError && (
                <div className="login__alert" role="alert" style={{ marginBottom: 14 }}>
                  {errorMessage(checkMutation.error)}
                </div>
              )}

              <ReadField label="Número" value={formatPhone(channel.displayPhone)} mono />
              <ReadField label="Phone Number ID" value={channel.phoneNumberId} mono />
              <ReadField label="WABA ID" value={channel.wabaId} mono />
              <ReadField
                label="Token"
                value={channel.hasToken ? `guardado · final ${channel.tokenHint}` : 'não informado'}
                mono
              />
              <ReadField
                label="App Secret"
                value={channel.hasAppSecret ? 'guardado' : 'não informado'}
                mono
              />
              <ReadField label="Segredo do webhook" value={channel.verifyToken} mono />
              <ReadField
                label="URL do webhook"
                value={channel.webhookUrl || 'defina PUBLIC_API_URL no servidor'}
                mono
              />
              {!channel.hasAppSecret && (
                <div className="login__alert" role="alert" style={{ marginTop: 14 }}>
                  <span>
                    <strong>Falta o App Secret.</strong> Sem ele o servidor não
                    tem como provar que o webhook veio mesmo da Meta, e recusa
                    tudo que chega — as confirmações de entrega e as respostas
                    não vão aparecer. Edite o canal e informe.
                  </span>
                </div>
              )}

              <p className="muted" style={{ fontSize: 12, marginTop: 12, color: T.inkFaint }}>
                A URL e o segredo acima são o que a Meta pede em Configuração →
                Webhooks. Nem o token nem o App Secret voltam por aqui: para
                trocá-los, edite o canal e informe os novos.
              </p>
            </div>

            <div className="card" style={{ padding: 18 }}>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: 14,
                }}
              >
                <div className="panel__title" style={{ margin: 0 }}>
                  Quem recebe
                </div>
                {can('canal-mensagens:editar') && (
                  <Button small icon={Plus} onClick={() => setNewRecipient(true)}>
                    Adicionar
                  </Button>
                )}
              </div>

              <p className="muted" style={{ fontSize: 12.5, marginTop: -6, marginBottom: 14 }}>
                A API oficial da Meta não manda para grupo — cada pessoa recebe
                no número dela. Em compensação, a auditoria sabe exatamente quem
                recebeu o quê.
              </p>

              {recipients.length === 0 && (
                <p className="muted" style={{ fontSize: 13 }}>
                  Ninguém cadastrado ainda.
                </p>
              )}

              {recipients.map((row) => (
                <div
                  key={row.id}
                  className="card"
                  style={{ padding: 10, marginBottom: 8, display: 'flex', gap: 10, alignItems: 'center' }}
                >
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 600 }}>
                      {row.name}
                      {!row.isActive && (
                        <span style={{ marginLeft: 8 }}>
                          <StatusBadge kind="mute">Desligado</StatusBadge>
                        </span>
                      )}
                    </div>
                    <div className="mono" style={{ fontSize: 11.5, color: T.inkFaint, marginTop: 2 }}>
                      {formatPhone(row.phone)}
                      {row.userName ? ` · responde como ${row.userName}` : ''}
                    </div>
                  </div>
                  {can('mensagens:criar') && row.isActive && (
                    <Button
                      small
                      variant="outline"
                      icon={Send}
                      onClick={() => setTesting(row)}
                    >
                      Enviar teste
                    </Button>
                  )}
                  {can('canal-mensagens:editar') && (
                    <Button
                      small
                      variant="ghost"
                      icon={Pencil}
                      aria-label={`Editar ${row.name}`}
                      title="Editar"
                      onClick={() => setEditingRecipient(row)}
                    />
                  )}
                  {can('canal-mensagens:excluir') && (
                    <Button
                      small
                      variant="ghost"
                      icon={Trash2}
                      aria-label={`Remover ${row.name}`}
                      title="Remover"
                      onClick={() => setRemovingRecipient(row)}
                    />
                  )}
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      <ChannelDrawer
        open={editing}
        channel={channel}
        onClose={() => setEditing(false)}
        onSaved={() => {
          setEditing(false);
          setCheck(null);
          refresh();
        }}
      />

      <RecipientDrawer
        open={newRecipient || Boolean(editingRecipient)}
        recipient={editingRecipient}
        onClose={() => {
          setNewRecipient(false);
          setEditingRecipient(null);
        }}
        onSaved={() => {
          setNewRecipient(false);
          setEditingRecipient(null);
          refresh();
        }}
      />

      <TestModal open={Boolean(testing)} recipient={testing} onClose={() => setTesting(null)} />

      <ConfirmDialog
        open={removing}
        destructive
        loading={removeMutation.isPending}
        title="Remover o canal de WhatsApp?"
        confirmLabel="Remover canal"
        error={removeMutation.error ? errorMessage(removeMutation.error) : undefined}
        message="O histórico de mensagens continua no sistema, mas nada mais será enviado até um canal novo ser configurado."
        onClose={() => {
          setRemoving(false);
          removeMutation.reset();
        }}
        onConfirm={() => removeMutation.mutate()}
      />

      <ConfirmDialog
        open={Boolean(removingRecipient)}
        destructive
        loading={removeRecipientMutation.isPending}
        title="Tirar da lista?"
        confirmLabel="Remover"
        message={
          <>
            <strong>{removingRecipient?.name}</strong> deixa de receber as
            mensagens do agente. As que ele já recebeu continuam no histórico.
          </>
        }
        onClose={() => {
          setRemovingRecipient(null);
          removeRecipientMutation.reset();
        }}
        onConfirm={() => removingRecipient && removeRecipientMutation.mutate(removingRecipient.id)}
      />
    </>
  );
}

function ChannelDrawer({
  open,
  channel,
  onClose,
  onSaved,
}: {
  open: boolean;
  channel: MessageChannel | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [name, setName] = useState('');
  const [displayPhone, setDisplayPhone] = useState('');
  const [phoneNumberId, setPhoneNumberId] = useState('');
  const [wabaId, setWabaId] = useState('');
  const [accessToken, setAccessToken] = useState('');
  const [appSecret, setAppSecret] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [wasOpen, setWasOpen] = useState(open);

  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) {
      setName(channel?.name ?? 'WhatsApp AMAI Park');
      setDisplayPhone(channel?.displayPhone ?? '');
      setPhoneNumberId(channel?.phoneNumberId ?? '');
      setWabaId(channel?.wabaId ?? '');
      setAccessToken('');
      setAppSecret('');
      setIsActive(channel?.isActive ?? true);
    }
  }

  const mutation = useMutation({
    mutationFn: (body: unknown) => api.put<MessageChannel>('/message-channel', body),
    onSuccess: onSaved,
  });

  const errors = fieldErrors(mutation.error);
  const canSubmit =
    name.trim().length >= 2 &&
    displayPhone.replace(/\D/g, '').length >= 10 &&
    phoneNumberId.trim().length >= 5 &&
    wabaId.trim().length >= 5 &&
    (Boolean(channel?.hasToken) || accessToken.trim().length > 0);

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title={channel ? 'Editar canal' : 'Configurar canal de WhatsApp'}
      description="Os dados saem do painel da Meta, em WhatsApp → Configuração da API"
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={mutation.isPending}>
            Cancelar
          </Button>
          <Button
            variant="primary"
            disabled={!canSubmit}
            loading={mutation.isPending}
            onClick={() =>
              mutation.mutate({
                kind: 'WHATSAPP_CLOUD',
                name: name.trim(),
                displayPhone,
                phoneNumberId: phoneNumberId.trim(),
                wabaId: wabaId.trim(),
                ...(accessToken.trim() ? { accessToken: accessToken.trim() } : {}),
                ...(appSecret.trim() ? { appSecret: appSecret.trim() } : {}),
                isActive,
              })
            }
          >
            Salvar canal
          </Button>
        </>
      }
    >
      {mutation.isError && (
        <div className="login__alert" role="alert" style={{ marginBottom: 14 }}>
          {errorMessage(mutation.error)}
        </div>
      )}

      <FormSection>
        <Field label="Nome do canal" htmlFor="ch-name" hint="Só para você reconhecer na tela">
          <input
            id="ch-name"
            className="input"
            value={name}
            maxLength={80}
            onChange={(event) => setName(event.target.value)}
          />
        </Field>

        <Field
          label="Número, com código do país"
          htmlFor="ch-phone"
          error={errors.displayPhone}
          hint="Ex.: 5568999331020 — sem o + e sem espaços"
        >
          <input
            id="ch-phone"
            className="input input--mono"
            value={displayPhone}
            onChange={(event) => setDisplayPhone(event.target.value)}
            placeholder="5568999331020"
          />
        </Field>

        <Field label="Phone Number ID" htmlFor="ch-pnid" error={errors.phoneNumberId}>
          <input
            id="ch-pnid"
            className="input input--mono"
            value={phoneNumberId}
            onChange={(event) => setPhoneNumberId(event.target.value)}
          />
        </Field>

        <Field label="WABA ID" htmlFor="ch-waba" error={errors.wabaId}>
          <input
            id="ch-waba"
            className="input input--mono"
            value={wabaId}
            onChange={(event) => setWabaId(event.target.value)}
          />
        </Field>

        <Field
          label="Token permanente"
          htmlFor="ch-token"
          error={errors.accessToken}
          hint={
            channel?.hasToken
              ? `Já existe um guardado (final ${channel.tokenHint}). Deixe vazio para mantê-lo.`
              : 'O token de System User, não o temporário de 24 horas'
          }
        >
          <input
            id="ch-token"
            className="input input--mono"
            type="password"
            autoComplete="off"
            value={accessToken}
            onChange={(event) => setAccessToken(event.target.value)}
            placeholder={channel?.hasToken ? '••••••••  (mantém o atual)' : ''}
          />
        </Field>

        <Field
          label="App Secret"
          htmlFor="ch-secret"
          error={errors.appSecret}
          hint={
            channel?.hasAppSecret
              ? 'Já existe um guardado. Deixe vazio para mantê-lo.'
              : 'Em Configurações básicas do aplicativo, no painel da Meta'
          }
        >
          <input
            id="ch-secret"
            className="input input--mono"
            type="password"
            autoComplete="off"
            value={appSecret}
            onChange={(event) => setAppSecret(event.target.value)}
            placeholder={channel?.hasAppSecret ? '••••••••  (mantém o atual)' : ''}
          />
        </Field>

        <Field label="Situação" htmlFor="ch-active">
          <select
            id="ch-active"
            className="input"
            value={isActive ? '1' : '0'}
            onChange={(event) => setIsActive(event.target.value === '1')}
          >
            <option value="1">Ligado — pode enviar</option>
            <option value="0">Desligado — não envia nada</option>
          </select>
        </Field>

        <p className="muted" style={{ fontSize: 12.5, color: T.inkFaint }}>
          O token é guardado criptografado e nunca mais aparece na tela. Quem
          puder ver este canal verá só os quatro últimos dígitos.
        </p>
      </FormSection>
    </Drawer>
  );
}

function RecipientDrawer({
  open,
  recipient,
  onClose,
  onSaved,
}: {
  open: boolean;
  recipient: MessageRecipient | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [wasOpen, setWasOpen] = useState(open);

  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) {
      setName(recipient?.name ?? '');
      setPhone(recipient?.phone ?? '');
      setIsActive(recipient?.isActive ?? true);
    }
  }

  const mutation = useMutation({
    mutationFn: (body: unknown) =>
      recipient
        ? api.patch<MessageRecipient>(`/message-recipients/${recipient.id}`, body)
        : api.post<MessageRecipient>('/message-recipients', body),
    onSuccess: onSaved,
  });

  const errors = fieldErrors(mutation.error);
  const canSubmit = name.trim().length >= 2 && phone.replace(/\D/g, '').length >= 10;

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title={recipient ? 'Editar destinatário' : 'Quem vai receber'}
      description="Cada pessoa recebe no número dela — a Meta não entrega em grupo"
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={mutation.isPending}>
            Cancelar
          </Button>
          <Button
            variant="primary"
            disabled={!canSubmit}
            loading={mutation.isPending}
            onClick={() => mutation.mutate({ name: name.trim(), phone, isActive })}
          >
            Salvar
          </Button>
        </>
      }
    >
      {mutation.isError && (
        <div className="login__alert" role="alert" style={{ marginBottom: 14 }}>
          {errorMessage(mutation.error)}
        </div>
      )}

      <FormSection>
        <Field label="Nome" htmlFor="rc-name" error={errors.name}>
          <input
            id="rc-name"
            className="input"
            value={name}
            maxLength={120}
            onChange={(event) => setName(event.target.value)}
            placeholder="Gabriel Moraes"
          />
        </Field>

        <Field
          label="WhatsApp, com código do país"
          htmlFor="rc-phone"
          error={errors.phone}
          hint="Ex.: 5568999331020 — sem o + e sem espaços"
        >
          <input
            id="rc-phone"
            className="input input--mono"
            value={phone}
            onChange={(event) => setPhone(event.target.value)}
            placeholder="5568999331020"
          />
        </Field>

        <Field label="Situação" htmlFor="rc-active">
          <select
            id="rc-active"
            className="input"
            value={isActive ? '1' : '0'}
            onChange={(event) => setIsActive(event.target.value === '1')}
          >
            <option value="1">Recebendo</option>
            <option value="0">Desligado — não recebe</option>
          </select>
        </Field>
      </FormSection>
    </Drawer>
  );
}

/**
 * O envio de teste, com as duas regras da Meta explicadas na tela.
 *
 * Sem essa explicação o operador manda texto livre, recebe "falhou" e conclui
 * que o sistema está quebrado — quando na verdade estava fora da janela de 24 h.
 */
function TestModal({
  open,
  recipient,
  onClose,
}: {
  open: boolean;
  recipient: MessageRecipient | null;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const [mode, setMode] = useState<'TEMPLATE' | 'TEXTO'>('TEMPLATE');
  const [text, setText] = useState('');
  const [sent, setSent] = useState<Message | null>(null);
  const [wasOpen, setWasOpen] = useState(open);

  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) {
      setMode('TEMPLATE');
      setText('');
      setSent(null);
    }
  }

  const mutation = useMutation({
    mutationFn: (body: unknown) => api.post<Message>('/messages/test', body),
    onSuccess: (message) => {
      setSent(message);
      void queryClient.invalidateQueries({ queryKey: ['messages'] });
    },
  });

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Enviar mensagem de teste"
      description={recipient ? `Para ${recipient.name} · ${formatPhone(recipient.phone)}` : ''}
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={mutation.isPending}>
            Fechar
          </Button>
          <Button
            variant="primary"
            icon={Send}
            loading={mutation.isPending}
            onClick={() =>
              recipient &&
              mutation.mutate({
                recipientId: recipient.id,
                mode,
                templateName: 'hello_world',
                templateLanguage: 'en_US',
                ...(mode === 'TEXTO' ? { text: text.trim() } : {}),
              })
            }
          >
            Enviar
          </Button>
        </>
      }
    >
      {mutation.isError && (
        <div className="login__alert" role="alert" style={{ marginBottom: 14 }}>
          {errorMessage(mutation.error)}
        </div>
      )}

      {sent && (
        <div
          className={sent.status === 'FALHOU' ? 'login__alert' : 'card'}
          role={sent.status === 'FALHOU' ? 'alert' : undefined}
          style={{ padding: 12, marginBottom: 14, fontSize: 12.5 }}
        >
          {sent.status === 'FALHOU' ? (
            sent.errorMessage
          ) : (
            <span style={{ color: T.green }}>
              Mensagem entregue ao WhatsApp. Confira o celular — e o histórico
              mostra a confirmação quando ela chegar.
            </span>
          )}
        </div>
      )}

      <FormSection>
        <Field label="Como mandar" htmlFor="tst-mode">
          <select
            id="tst-mode"
            className="input"
            value={mode}
            onChange={(event) => setMode(event.target.value as 'TEMPLATE' | 'TEXTO')}
          >
            <option value="TEMPLATE">Modelo hello_world (funciona sempre)</option>
            <option value="TEXTO">Texto livre (só dentro da janela de 24 h)</option>
          </select>
        </Field>

        {mode === 'TEXTO' && (
          <Field label="Mensagem" htmlFor="tst-text">
            <textarea
              id="tst-text"
              className="input"
              rows={3}
              maxLength={1000}
              value={text}
              onChange={(event) => setText(event.target.value)}
              placeholder="Teste do AMAI OPS."
            />
          </Field>
        )}

        <p className="muted" style={{ fontSize: 12.5, color: T.inkFaint }}>
          {mode === 'TEMPLATE' ? (
            <>
              O <strong>hello_world</strong> já vem pronto em toda conta nova da
              Meta. Serve para provar que o canal funciona sem esperar aprovação
              de modelo nenhum.
            </>
          ) : (
            <>
              A Meta só entrega texto livre se a pessoa escreveu para este número
              nas últimas <strong>24 horas</strong>. Fora disso ela recusa — e é
              por isso que o resumo de segunda vai por modelo.
            </>
          )}
        </p>
      </FormSection>
    </Modal>
  );
}
